import React, { useState, useRef, useEffect, act } from "react";
import Dropdown from "@/components/ui/Dropdown";
import FilterInputs from "@/components/filters/FilterInputs";
import { ChevronDown, RefreshCw } from "lucide-react";
const ApplicationFilters = ({
  columns,
  visibleColumns,
  processedApplications,
  onFiltersChange,
  filtersStorageKey = "filters",
  activeFiltersStorageKey = "activeFilters",
  getFiltersComponent = null,
  filterInputTriggerClasses = "",
  filterInputsContainerClasses = "",
}) => {
  const [showFilterToggle, setShowFilterToggle] = useState(false);
  const [filters, setFilters] = useState(() => {
    const saved = localStorage.getItem(filtersStorageKey);
    return saved ? JSON.parse(saved) : {};
  });
  const [activeFilters, setActiveFilters] = useState(() => {
    const saved = localStorage.getItem(activeFiltersStorageKey);
    return saved ? JSON.parse(saved) : [];
  });
  const filterToggleRef = useRef(null);
  const [filtersComponent, setFiltersComponent] = useState(null);

  // Refresh filters from localStorage when storage keys change
  useEffect(() => {
    const saved = localStorage.getItem(filtersStorageKey);
    const savedActive = localStorage.getItem(activeFiltersStorageKey);

    if (saved) {
      setFilters(JSON.parse(saved));
    }

    if (savedActive) {
      setActiveFilters(JSON.parse(savedActive));
    }
  }, [filtersStorageKey, activeFiltersStorageKey]);

  // Listen for storage changes to sync with external filter updates
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === filtersStorageKey && e.newValue) {
        setFilters(JSON.parse(e.newValue));
      }
      if (e.key === activeFiltersStorageKey && e.newValue) {
        setActiveFilters(JSON.parse(e.newValue));
      }
    };

    window.addEventListener("storage", handleStorageChange);
    return () => {
      window.removeEventListener("storage", handleStorageChange);
    };
  }, [filtersStorageKey, activeFiltersStorageKey]);

  // Check localStorage for updates when component becomes visible (optimized approach)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        const currentFilters = localStorage.getItem(filtersStorageKey);
        const currentActiveFilters = localStorage.getItem(
          activeFiltersStorageKey,
        );

        if (currentFilters && JSON.stringify(filters) !== currentFilters) {
          setFilters(JSON.parse(currentFilters));
        }

        if (
          currentActiveFilters &&
          JSON.stringify(activeFilters) !== currentActiveFilters
        ) {
          setActiveFilters(JSON.parse(currentActiveFilters));
        }
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () =>
      document.removeEventListener("visibilitychange", handleVisibilityChange);
  }, [filters, activeFilters, filtersStorageKey, activeFiltersStorageKey]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        filterToggleRef.current &&
        !filterToggleRef.current.contains(event.target)
      ) {
        setShowFilterToggle(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    localStorage.setItem(filtersStorageKey, JSON.stringify(filters));
    onFiltersChange(filters, activeFilters);
  }, [filters]);

  useEffect(() => {
    localStorage.setItem(
      activeFiltersStorageKey,
      JSON.stringify(activeFilters),
    );
    onFiltersChange(filters, activeFilters);
  }, [activeFilters]);

  // start
  // code related to: When a column is toggled off in the Columns dropdown, any active filters for that column should be removed automatically.
  // Check if any active filters are for columns that are no longer visible
  useEffect(() => {
    // Ensure we have both visibleColumns and activeFilters
    if (visibleColumns && activeFilters.length > 0) {
      // Find filters that are active but their columns are not visible
      const filtersToRemove = activeFilters.filter((filterKey) => {
        // For regular columns
        if (!visibleColumns[filterKey]) {
          return true;
        }

        // For custom fields with field_ prefix
        if (filterKey.startsWith("field_")) {
          const columnKey = filterKey;
          return !visibleColumns[columnKey];
        }

        return false;
      });

      // Remove those filters
      if (filtersToRemove.length > 0) {
        const newActiveFilters = activeFilters.filter(
          (filter) => !filtersToRemove.includes(filter),
        );
        setActiveFilters(newActiveFilters);

        // Also remove from filters object
        const newFilters = { ...filters };
        filtersToRemove.forEach((key) => {
          delete newFilters[key];
        });
        setFilters(newFilters);
      }
    }
  }, [visibleColumns]);
  // end
  const addFilter = (filterKey) => {
    if (!activeFilters.includes(filterKey)) {
      const newActiveFilters = [...activeFilters, filterKey];
      setActiveFilters(newActiveFilters);
    }
  };

  const removeFilter = (filterKey) => {
    const newActiveFilters = activeFilters.filter((key) => key !== filterKey);
    setActiveFilters(newActiveFilters);

    setFilters((prev) => {
      const newFilters = { ...prev };
      delete newFilters[filterKey];
      return newFilters;
    });
  };

  /*==========================================*/
  const setFilterComponent = () => {
    const filteredColumns = columns.filter(
      (column) => visibleColumns[column.key],
    );

    const tempFilterComp = (
      <FilterInputs
        // processedApplications={[...processedApplications]}
        activeFilters={activeFilters}
        columns={columns}
        filters={filters}
        setFilters={setFilters}
        setActiveFilters={setActiveFilters}
        filterInputTriggerClasses={filterInputTriggerClasses}
        containerClasses={filterInputsContainerClasses}
      />
    );

    if (getFiltersComponent && typeof getFiltersComponent === "function") {
      getFiltersComponent(tempFilterComp);
      setFiltersComponent(null);
    } else {
      setFiltersComponent(tempFilterComp);
    }
  };
  useEffect(() => {
    setFilterComponent();
  }, [activeFilters, filters, visibleColumns]);
  /*==========================================*/

  const handleFilterToggle = (e, filterKey) => {
    e.preventDefault();
    e.stopPropagation();

    if (activeFilters.includes(filterKey)) {
      removeFilter(filterKey);
    } else {
      addFilter(filterKey);
    }
  };

  return (
    <div className="flex flex-wrap items-center gap-2">
      {filtersComponent}

      <div className="relative" ref={filterToggleRef}>
        <button
          className="w-40 h-[36px] px-3 py-2 bg-primary-dark rounded-lg shadow-[0px_1px_2px_0px_rgba(10,13,18,0.05),inset_0px_-2px_0px_0px_rgba(10,13,18,0.05),inset_0px_0px_0px_1px_rgba(10,13,18,0.18)] outline outline-2 outline-offset-[-2px] outline-white/10 flex justify-center items-center gap-1 overflow-hidden group hover:bg-primary-dark transition-all"
          onClick={() => setShowFilterToggle(!showFilterToggle)}
        >
          <span className="text-white text-sm font-semibold font-['Inter'] leading-5">
            Add Filter
          </span>
          <ChevronDown className="h-4 w-4 text-white" />
        </button>
        {showFilterToggle && (
          <div className="absolute right-0 top-12 z-50 mt-2 bg-card shadow-lg rounded-md border p-3 min-w-[200px]  max-w-[300px]">
            <div className="font-medium pb-2 border-b mb-2">Filter Options</div>
            {(() => {
              const filteredColumns = columns.filter(
                (column) =>
                  column.filterable &&
                  visibleColumns[column.key] &&
                  (column.options !== null ||
                    !column.customField ||
                    column.isField),
              );
              console.log(
                "Add Filter Dropdown - Available columns:",
                filteredColumns,
              );
              return filteredColumns;
            })().map((column) => {
              const isChecked = activeFilters.includes(column.key);

              // Console log only for custom job fields
              if (column.customField && column.key.includes("job")) {
                console.log(
                  `Custom Job Field - ${column.key} (${column.label}): checked=${isChecked}, activeFilters:`,
                  activeFilters,
                );
              }

              return (
                <div
                  key={column.key}
                  className={`flex items-center gap-3 py-2 px-3 hover:bg-muted rounded-md cursor-pointer transition-colors ${isChecked ? "bg-muted" : ""}`}
                  onClick={(e) => handleFilterToggle(e, column.key)}
                >
                  <div
                    className={`w-4 h-4 relative rounded border flex items-center justify-center shrink-0 transition-all ${
                      isChecked
                        ? "bg-secondary border-secondary"
                        : "bg-card border-input"
                    }`}
                  >
                    {isChecked && (
                      <svg
                        width="10"
                        height="10"
                        viewBox="0 0 12 12"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M10 3L4.5 8.5L2 6"
                          stroke="white"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    )}
                  </div>
                  <span
                    className={`text-sm ${isChecked ? "font-semibold text-foreground" : "text-muted-foreground"}`}
                  >
                    {column.label}
                  </span>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default ApplicationFilters;
